import { useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import { Button, type IButtonProps } from '~/components/ui';
import { toastGraphQLError } from '~/utils';
import { ConfirmDialog } from './ConfirmDialog';

export type ConfirmActionButtonProps = Readonly<{
  triggerLabel: ReactNode;
  triggerProps?: Omit<IButtonProps, 'children' | 'onClick'>;
  title: ReactNode;
  message: ReactNode;
  cancelLabel: string;
  confirmLabel: string;
  successMessage: string;
  isPending: boolean;
  onConfirm: () => Promise<unknown>;
}>;

export function ConfirmActionButton({
  triggerLabel,
  triggerProps,
  title,
  message,
  cancelLabel,
  confirmLabel,
  successMessage,
  isPending,
  onConfirm,
}: ConfirmActionButtonProps) {
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = async () => {
    try {
      await onConfirm();
      toast.success(successMessage);
      setIsConfirming(false);
    } catch (error) {
      toastGraphQLError(error);
    }
  };

  return (
    <>
      <Button
        size="sm"
        width="auto"
        {...triggerProps}
        aria-haspopup="dialog"
        onClick={() => setIsConfirming(true)}
      >
        {triggerLabel}
      </Button>
      <ConfirmDialog
        isOpen={isConfirming}
        title={title}
        message={message}
        cancelLabel={cancelLabel}
        confirmLabel={confirmLabel}
        isPending={isPending}
        onCancel={() => setIsConfirming(false)}
        onConfirm={() => void handleConfirm()}
      />
    </>
  );
}
